import React, { useState } from "react";
import { Alert, Button, Space, Typography } from "antd";
import { toast } from "../../utils/toast";
import {
  DownloadOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { StatItem, StatStrip } from "./reportCommon";

const { Title } = Typography;

interface ReportShellProps {
  title: string;
  loading: boolean;
  error?: string | null;
  stats?: StatItem[];
  /** Filter controls rendered in the toolbar */
  filters?: React.ReactNode;
  onRefresh: () => void;
  onReset?: () => void;
  onExportCsv?: () => Promise<void> | void;
  onExportPdf?: () => Promise<void> | void;
  /** Extra content between stats and table (e.g. breakdown chips) */
  extra?: React.ReactNode;
  children: React.ReactNode;
}

const ReportShell: React.FC<ReportShellProps> = ({
  title,
  loading,
  error,
  stats,
  filters,
  onRefresh,
  onReset,
  onExportCsv,
  onExportPdf,
  extra,
  children,
}) => {
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);

  const runExport = async (kind: "csv" | "pdf") => {
    const fn = kind === "csv" ? onExportCsv : onExportPdf;
    if (!fn) return;
    setExporting(kind);
    try {
      await fn();
      toast.success(`${kind.toUpperCase()} exported successfully`);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || `Failed to export ${kind.toUpperCase()}`
      );
    } finally {
      setExporting(null);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        <Space wrap>
          {onReset && (
            <Button size="small" icon={<ClearOutlined />} onClick={onReset}>
              Reset
            </Button>
          )}
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={onRefresh}
          >
            Refresh
          </Button>
          {onExportCsv && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={exporting === "csv"}
              onClick={() => runExport("csv")}
            >
              CSV
            </Button>
          )}
          {onExportPdf && (
            <Button
              size="small"
              icon={<FilePdfOutlined />}
              loading={exporting === "pdf"}
              onClick={() => runExport("pdf")}
            >
              PDF
            </Button>
          )}
        </Space>
      </div>

      {filters && (
        <Space wrap style={{ marginBottom: 12 }} size={[8, 8]}>
          {filters}
        </Space>
      )}

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button size="small" onClick={onRefresh}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      ) : (
        <>
          {stats && stats.length > 0 && (
            <StatStrip stats={stats} loading={loading} />
          )}
          {extra}
        </>
      )}

      {children}
    </div>
  );
};

export default ReportShell;
